import React, { useState, useEffect, useRef } from 'react';
import { Brain, Zap, Bot, Settings, BarChart, CheckCircle, Rocket } from 'lucide-react';

type SetupStep = {
	id: number;
	title: string;
	description: string;
	icon: JSX.Element;
	color: string;
};

type CampaignLaunchLoadingProps = {
	onFinish?: () => void;
	campaignStatus?: {
		isLoading: boolean;
		progress: number;
		error?: string;
	};
	scheduleStatus?: {
		isLoading: boolean;
		progress: number;
		error?: string;
	};
	autoRedirectDelay?: number; // Time in ms before auto-redirecting
	handleBack: () => void;
};

const CampaignLaunchLoading: React.FC<CampaignLaunchLoadingProps> = ({
	onFinish,
	campaignStatus = { isLoading: true, progress: 0 },
	scheduleStatus = { isLoading: true, progress: 0 },
	autoRedirectDelay = 5000,
	handleBack,
}) => {
	const [progress, setProgress] = useState(0);
	const [currentStep, setCurrentStep] = useState(0);
	const [isCompleting, setIsCompleting] = useState(false);
	const [completedSteps, setCompletedSteps] = useState<number[]>([]);
	const [showConfetti, setShowConfetti] = useState(false);
	const [countdown, setCountdown] = useState(autoRedirectDelay / 1000);
	const autoRedirectTimer = useRef<NodeJS.Timeout | null>(null);
	const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
	const isRedirecting = useRef<boolean>(false);
	const completeTimerRef = useRef<NodeJS.Timeout | null>(null);
	const forceCompletionTimerRef = useRef<NodeJS.Timeout | null>(null);

	const setupSteps: SetupStep[] = [
		{
			id: 0,
			title: 'INITIALIZING CAMPAIGN',
			description: 'Setting up your campaign infrastructure...',
			icon: <Rocket className="h-10 w-10" />,
			color: 'from-blue-500 to-blue-600',
		},
		{
			id: 1,
			title: 'TRAINING AI MODEL',
			description: 'Configuring AI to match your campaign goals...',
			icon: <Bot className="h-10 w-10" />,
			color: 'from-purple-500 to-purple-600',
		},
		{
			id: 2,
			title: 'OPTIMIZING PARAMETERS',
			description: 'Fine-tuning conversation flows and responses...',
			icon: <Settings className="h-10 w-10" />,
			color: 'from-green-500 to-green-600',
		},
		{
			id: 3,
			title: 'BUILDING ANALYTICS',
			description: 'Setting up real-time reporting dashboard...',
			icon: <BarChart className="h-10 w-10" />,
			color: 'from-yellow-500 to-yellow-600',
		},
		{
			id: 4,
			title: 'FINAL PREPARATIONS',
			description: 'Connecting communication channels...',
			icon: <Zap className="h-10 w-10" />,
			color: 'from-blue-500 to-blue-600',
		},
		{
			id: 5,
			title: 'LAUNCH COMPLETE!',
			description: 'Your campaign is ready to go!',
			icon: <CheckCircle className="h-10 w-10" />,
			color: 'from-green-500 to-green-600',
		},
	];

	// Main effect to handle progress calculation and completion logic
	useEffect(() => {
		if (campaignStatus.error || scheduleStatus.error) return;
		if (isRedirecting.current) return; // Skip progress updates if already redirecting

		const campaignWeight = 0.6;
		const scheduleWeight = 0.4;

		const campaignProgress = !campaignStatus.isLoading ? 100 : campaignStatus.progress;
		const schedProgress = !scheduleStatus.isLoading ? 100 : scheduleStatus.progress;

		const totalProgress = campaignProgress * campaignWeight + schedProgress * scheduleWeight;

		if (!campaignStatus.isLoading && !scheduleStatus.isLoading && !isCompleting) {
			setIsCompleting(true);

			const startValue = totalProgress;
			// Extend the animation duration to ensure smooth completion
			const animationDuration = 2000;
			const endTime = Date.now() + animationDuration;
			const startTime = Date.now();

			// Clean up any existing timers first
			if (completeTimerRef.current) {
				clearInterval(completeTimerRef.current);
				completeTimerRef.current = null;
			}

			if (forceCompletionTimerRef.current) {
				clearTimeout(forceCompletionTimerRef.current);
				forceCompletionTimerRef.current = null;
			}

			// Force completion after animation duration + buffer, regardless of other logic
			// This ensures we don't get stuck at any percentage
			forceCompletionTimerRef.current = setTimeout(() => {
				if (progress < 100) {
					setProgress(100);

					// Only trigger confetti if it hasn't been shown yet
					if (!showConfetti) {
						setTimeout(() => {
							setShowConfetti(true);
							isRedirecting.current = true;

							setupRedirect();
						}, 300);
					}
				}
			}, animationDuration + 500); // Add buffer to ensure animation had time to complete

			completeTimerRef.current = setInterval(() => {
				const now = Date.now();
				const timeProgress = Math.min(1, (now - startTime) / (endTime - startTime));

				// Exponential easing to ensure smoother approach to 100%
				const easedProgress = 1 - Math.pow(1 - timeProgress, 2);
				const newProgress = startValue + (100 - startValue) * easedProgress;

				// Always set progress to exactly 100 when animation completes
				if (timeProgress >= 1) {
					setProgress(100);
					clearInterval(completeTimerRef.current!);
					completeTimerRef.current = null;

					// Only show confetti and set up redirect AFTER progress is fully 100%
					setTimeout(() => {
						setShowConfetti(true);
						isRedirecting.current = true; // Mark as redirecting to prevent progress changes

						setupRedirect();
					}, 300); // Small delay to ensure UI has updated with 100% progress
				} else {
					// Make sure we're always moving forward in progress
					setProgress((prev) => Math.max(prev, newProgress));
				}
			}, 30); // More frequent updates for smoother animation

			return () => {
				if (completeTimerRef.current) clearInterval(completeTimerRef.current);
				if (forceCompletionTimerRef.current) clearTimeout(forceCompletionTimerRef.current);
				if (autoRedirectTimer.current) clearTimeout(autoRedirectTimer.current);
				if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
			};
		} else if (!isCompleting) {
			// Only update progress if we're not in the completion animation
			setProgress(totalProgress);
		}
	}, [
		campaignStatus,
		scheduleStatus,
		onFinish,
		isCompleting,
		autoRedirectDelay,
		progress,
		showConfetti,
	]);

	// Helper function to set up the redirect
	const setupRedirect = () => {
		if (!onFinish) return;

		// Clear any existing timers first
		if (autoRedirectTimer.current) clearTimeout(autoRedirectTimer.current);
		if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

		autoRedirectTimer.current = setTimeout(() => {
			onFinish();
		}, autoRedirectDelay);

		countdownTimerRef.current = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(countdownTimerRef.current!);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	};

	useEffect(() => {
		const stepIndex = Math.min(Math.floor((progress / 100) * 5), 5);

		if (stepIndex > currentStep) {
			setCompletedSteps((prev) => {
				const newCompleted = [...prev];
				for (let i = currentStep + 1; i <= stepIndex; i++) {
					if (!newCompleted.includes(i)) {
						newCompleted.push(i);
					}
				}
				return newCompleted;
			});
		}

		setCurrentStep(stepIndex);
	}, [progress, currentStep]);

	useEffect(() => {
		const fallbackTimer = setTimeout(() => {
			if (campaignStatus.isLoading || scheduleStatus.isLoading) {
				const slowProgress = setInterval(() => {
					setProgress((prev) => {
						if (prev >= 95) {
							clearInterval(slowProgress);
							return prev;
						}
						return prev + 5;
					});
				}, 5000);

				return () => clearInterval(slowProgress);
			}
		}, 20000);

		return () => clearTimeout(fallbackTimer);
	}, [campaignStatus.isLoading, scheduleStatus.isLoading]);

	// Cleanup function to ensure timers are cleared on unmount
	useEffect(() => {
		return () => {
			if (autoRedirectTimer.current) clearTimeout(autoRedirectTimer.current);
			if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
			if (completeTimerRef.current) clearInterval(completeTimerRef.current);
			if (forceCompletionTimerRef.current) clearTimeout(forceCompletionTimerRef.current);
		};
	}, []);

	// Effect to debug and fix progress stuck at exactly 96%
	useEffect(() => {
		// If progress is stuck at 96% for more than 1 second, force it to 100%
		if (progress > 95 && progress < 100) {
			const stuckTimer = setTimeout(() => {
				setProgress(100);

				// If confetti hasn't shown yet, show it after progress hits 100%
				if (!showConfetti) {
					setTimeout(() => {
						setShowConfetti(true);
						isRedirecting.current = true;

						setupRedirect();
					}, 300);
				}
			}, 1000);

			return () => clearTimeout(stuckTimer);
		}
	}, [progress, showConfetti]);

	const Confetti = () => (
		<div className="fixed inset-0 pointer-events-none z-50">
			{Array.from({ length: 50 }).map((_, i) => {
				const size = Math.random() * 10 + 5;
				const left = Math.random() * 100;
				const animationDuration = Math.random() * 3 + 2;
				const delay = Math.random() * 0.5;

				return (
					<div
						key={i}
						className="absolute top-0 rounded-full"
						style={{
							left: `${left}%`,
							width: `${size}px`,
							height: `${size}px`,
							backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
							animation: `confetti ${animationDuration}s ease-in ${delay}s forwards`,
						}}
					/>
				);
			})}
		</div>
	);

	return (
		<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden relative">
			{/* Animated background particles */}
			<div className="absolute inset-0 overflow-hidden">
				{Array.from({ length: 20 }).map((_, i) => {
					const size = Math.random() * 6 + 2;
					const top = Math.random() * 100;
					const left = Math.random() * 100;
					const duration = Math.random() * 50 + 10;

					return (
						<div
							key={i}
							className="absolute rounded-full bg-blue-500 opacity-10"
							style={{
								top: `${top}%`,
								left: `${left}%`,
								width: `${size}px`,
								height: `${size}px`,
								boxShadow: '0 0 20px 2px rgba(59, 130, 246, 0.3)',
								animation: `float ${duration}s linear infinite`,
							}}
						/>
					);
				})}
			</div>

			{showConfetti && <Confetti />}

			<div className="w-full max-w-lg px-6 py-12 relative z-10">
				<div className="flex justify-center mb-12">
					<div className="relative flex items-center justify-center w-48 h-48">
						{/* Animated rings */}
						<div className="absolute w-48 h-48 rounded-full border-4 border-blue-500 animate-ping opacity-20"></div>
						<div className="absolute w-40 h-40 rounded-full border-4 border-purple-500 animate-pulse opacity-30"></div>

						{/* Main icon background */}
						<div className="relative flex items-center justify-center w-32 h-32 bg-gradient-to-br from-blue-600 to-purple-700 rounded-full shadow-xl">
							<Brain className="h-16 w-16 text-white drop-shadow-lg" />
						</div>
					</div>
				</div>

				<div className="mb-10 text-center">
					<h2 className="text-4xl font-bold text-white mb-3 tracking-wider animate-pulse">
						{setupSteps[currentStep].title}
					</h2>
					<p className="text-gray-300 text-lg">{setupSteps[currentStep].description}</p>

					{/* Show error message if there's an error */}
					{(campaignStatus.error || scheduleStatus.error) && (
						<p className="text-red-400 mt-4 bg-red-900/20 p-3 rounded-lg border border-red-800">
							{campaignStatus.error || scheduleStatus.error}
						</p>
					)}

					{/* Show auto-redirect message when complete */}
					{progress >= 100 && showConfetti && (
						<p className="text-blue-300 mt-4 text-sm">
							Automatically redirecting in {countdown} seconds...
						</p>
					)}
				</div>

				{/* Progress bar */}
				<div className="mb-10">
					<div className="h-4 w-full bg-gray-800 rounded-full overflow-hidden shadow-inner">
						<div
							className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full transition-all duration-300 ease-out"
							style={{ width: `${progress}%` }}
						>
							<div className="h-full w-20 animate-pulse bg-white opacity-20"></div>
						</div>
					</div>
					<div className="flex justify-between mt-3">
						<span className="text-blue-400 text-xl font-medium">PROGRESS</span>
						<span className="text-purple-400 text-xl font-medium">{Math.round(progress)}%</span>
					</div>
				</div>

				{/* Steps indicator */}
				<div className="mb-10">
					<div className="flex justify-between relative">
						{/* Line connecting dots */}
						<div className="absolute top-4 left-0 right-0 h-1 bg-gray-700"></div>

						{/* Step dots */}
						{setupSteps.map((step) => (
							<div key={step.id} className="relative z-10 flex flex-col items-center">
								<div
									className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
										currentStep >= step.id
											? `bg-gradient-to-br ${step.color} shadow-lg`
											: 'bg-gray-700'
									}`}
								>
									{completedSteps.includes(step.id) ? (
										<CheckCircle className="h-4 w-4 text-white animate-pulse" />
									) : (
										<div
											className={`w-2 h-2 rounded-full ${currentStep >= step.id ? 'bg-white' : 'bg-gray-500'}`}
										></div>
									)}
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Button */}
				<div className="text-center">
					{campaignStatus.error ? (
						<button
							onClick={handleBack}
							className="px-10 py-4 rounded-lg text-white font-bold text-xl tracking-wider transition-all"
						>
							Cancel
						</button>
					) : (
						<button
							className={`px-10 py-4 rounded-lg text-white font-bold text-xl tracking-wider transition-all ${
								progress >= 100 && showConfetti
									? 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 transform hover:scale-105 shadow-lg animate-pulse'
									: 'bg-gray-700 cursor-not-allowed opacity-50'
							}`}
							disabled={!(progress >= 100 && showConfetti)}
							onClick={progress >= 100 && showConfetti ? onFinish : undefined}
						>
							{progress >= 100 && showConfetti ? 'START CAMPAIGN' : 'LAUNCHING...'}
						</button>
					)}
				</div>
			</div>

			{/* Add CSS animations */}
			<style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-30px) translateX(15px);
          }
          50% {
            transform: translateY(-15px) translateX(30px);
          }
          75% {
            transform: translateY(-40px) translateX(-15px);
          }
        }
        
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
		</div>
	);
};

export default CampaignLaunchLoading;
